import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { blockBackdropClose, FormDialogTitle, LoadingState, useErrorToast } from '../common';
import {
  CERTIFICATE_FONT_ACCEPT,
  CERTIFICATE_FONT_MAX_FILE_SIZE,
  CERTIFICATE_FONT_NAME_MAX_LENGTH,
  certificateFontService,
  type CertificateFont,
} from '../../services/certificateFontService';

type CertificateFontDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

const FONT_EXTENSION_PATTERN = /\.(ttf|otf)$/i;

function getFontExtension(font: CertificateFont) {
  const match = FONT_EXTENSION_PATTERN.exec(font.fileUrl.split('?')[0]);
  return match ? match[1].toUpperCase() : null;
}

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

function validateFontFile(file: File) {
  if (!FONT_EXTENSION_PATTERN.test(file.name)) {
    return 'ttf 또는 otf 파일만 등록할 수 있습니다.';
  }
  if (file.size > CERTIFICATE_FONT_MAX_FILE_SIZE) {
    return '폰트 파일은 20MB를 초과할 수 없습니다.';
  }
  return null;
}

function CertificateFontDialog({ open, onClose }: CertificateFontDialogProps) {
  const [fonts, setFonts] = useState<CertificateFont[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CertificateFont | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showError, errorToast } = useErrorToast();

  useEffect(() => {
    if (!open) {
      return;
    }

    let ignore = false;
    setLoading(true);
    certificateFontService
      .list()
      .then((data) => {
        if (!ignore) {
          setFonts(data);
        }
      })
      .catch((error) => {
        if (!ignore) {
          showError(error, '폰트 목록을 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, showError]);

  const resetForm = () => {
    setName('');
    setFile(null);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    resetForm();
    onClose();
  };

  const handlePickFile = (picked: File | null) => {
    if (!picked) {
      return;
    }

    const message = validateFontFile(picked);
    if (message) {
      showError(new Error(message));
      return;
    }

    setFile(picked);
    // 이름을 아직 안 적었다면 확장자를 뗀 파일명을 기본값으로 채워 준다
    setName((current) =>
      current.trim()
        ? current
        : picked.name.replace(FONT_EXTENSION_PATTERN, '').slice(0, CERTIFICATE_FONT_NAME_MAX_LENGTH)
    );
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showError(new Error('폰트 이름을 입력해 주세요.'));
      return;
    }
    if (!file) {
      showError(new Error('폰트 파일을 선택해 주세요.'));
      return;
    }

    setSubmitting(true);
    try {
      const created = await certificateFontService.create(trimmedName, file);
      setFonts((current) => [...current, created]);
      resetForm();
    } catch (error) {
      showError(error, '폰트를 등록하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }

    const target = deleteTarget;
    setDeleting(true);
    try {
      await certificateFontService.delete(target.id);
      setFonts((current) => current.filter((font) => font.id !== target.id));
    } catch (error) {
      // 템플릿이 사용 중인 폰트면 서버가 사유를 담은 400 메시지를 돌려준다
      showError(error, '폰트를 삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(handleClose)} open={open}>
      <FormDialogTitle onClose={handleClose} sx={{ fontSize: 18, fontWeight: 800 }}>
        폰트 관리
      </FormDialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: '#0f172a', fontSize: 14, fontWeight: 800, mb: 1.5 }}>새 폰트 추가</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
          <TextField
            fullWidth
            label="폰트 이름"
            onChange={(event) => setName(event.target.value)}
            placeholder="템플릿에서 선택할 때 보이는 이름"
            required
            size="small"
            inputProps={{ maxLength: CERTIFICATE_FONT_NAME_MAX_LENGTH }}
            value={name}
          />
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
            <Button onClick={() => fileInputRef.current?.click()} size="small" variant="outlined">
              {file ? '파일 변경' : '폰트 파일 선택'}
            </Button>
            <Typography
              sx={{ color: file ? '#0f172a' : '#94a3b8', fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {file ? `${file.name} (${formatFileSize(file.size)})` : 'ttf, otf · 최대 20MB'}
            </Typography>
            <input
              accept={CERTIFICATE_FONT_ACCEPT}
              hidden
              onChange={(event) => {
                handlePickFile(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
              ref={fileInputRef}
              type="file"
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button disabled={submitting} disableElevation onClick={handleSubmit} variant="contained">
              {submitting ? '등록 중...' : '폰트 추가'}
            </Button>
          </Box>
        </Box>

        <Typography sx={{ color: '#0f172a', fontSize: 14, fontWeight: 800, mb: 1.5 }}>
          등록된 폰트 ({fonts.length})
        </Typography>
        {loading ? (
          <LoadingState message="폰트 목록을 불러오는 중입니다." />
        ) : fonts.length === 0 ? (
          <Paper sx={{ color: '#64748b', fontSize: 13, p: 3, textAlign: 'center' }} variant="outlined">
            등록된 폰트가 없습니다.
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gap: 1, maxHeight: 280, overflowY: 'auto' }}>
            {fonts.map((font) => {
              const extension = getFontExtension(font);

              return (
                <Paper
                  key={font.id}
                  sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between', px: 2, py: 1.25 }}
                  variant="outlined"
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {font.name}
                  </Typography>
                  <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 1 }}>
                    {extension && <Chip label={extension} size="small" variant="outlined" />}
                    <Button
                      color="error"
                      onClick={() => setDeleteTarget(font)}
                      size="small"
                      variant="outlined"
                    >
                      삭제
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>닫기</Button>
      </DialogActions>

      <Dialog fullWidth maxWidth="xs" onClose={() => !deleting && setDeleteTarget(null)} open={deleteTarget !== null}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>폰트 삭제</DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
            <strong>{deleteTarget?.name}</strong> 폰트를 삭제할까요? 삭제 후 되돌릴 수 없습니다.
            <br />
            완주증 템플릿에서 사용 중인 폰트는 삭제할 수 없습니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>
            취소
          </Button>
          <Button color="error" disabled={deleting} disableElevation onClick={handleDeleteConfirm} variant="contained">
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
      {errorToast}
    </Dialog>
  );
}

export default CertificateFontDialog;
